import { QueryClient, useQuery, type UseQueryResult } from "@tanstack/react-query";

import { isActiveState } from "@/lib/run-states";

import { api, ApiError, unwrap, type Schemas } from "./client";

export type Library = Schemas["Library"];
export type LibraryMachine = Schemas["LibraryMachine"];
export type Procedure = Schemas["Procedure"];
export type ReviewItem = Schemas["ReviewItem"];
export type RunSummary = Schemas["RunSummary"];
export type RunDetails = Schemas["RunDetails"];
export type StageInfo = Schemas["StageInfo"];
export type Me = Schemas["Me"];

/**
 * Query-key conventions. Every key starts with its resource; run keys nest under the run id so
 * `invalidateQueries({ queryKey: queryKeys.run(id) })` refreshes the summary and the details.
 */
export const queryKeys = {
  me: ["me"] as const,
  stages: ["stages"] as const,
  library: ["library"] as const,
  run: (runId: string) => ["runs", runId] as const,
  runSummary: (runId: string) => ["runs", runId, "summary"] as const,
  runDetails: (runId: string) => ["runs", runId, "details"] as const,
};

/** Polling: 5 s while a run is active or something is processing, 30 s otherwise. */
export const POLL_ACTIVE_MS = 5_000;
export const POLL_IDLE_MS = 30_000;

export function pollInterval(active: boolean): number {
  return active ? POLL_ACTIVE_MS : POLL_IDLE_MS;
}

/** A run is active while a job is queued/running/pending or a stage is still processing. */
export function runIsActive(summary: RunSummary | null | undefined): boolean {
  if (!summary) return false;
  if (isActiveState(summary.state) || summary.running) return true;
  return summary.stages.some((s) => s.state === "running");
}

/** The library is active while any machine is processing or any procedure is still running. */
export function libraryIsActive(library: Library | null | undefined): boolean {
  if (!library) return false;
  return library.machines.some(
    (machine) =>
      machine.flag === "processing" ||
      machine.procedures.some((procedure) => isActiveState(procedure.state)),
  );
}

/** Don't retry client errors (4xx); retry server and network errors twice. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
  return failureCount < 2;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        staleTime: 5_000,
        refetchOnWindowFocus: true,
        // Polling continues in the background so the library's pills stay current.
        refetchIntervalInBackground: false,
      },
    },
  });
}

// ---- Query functions (also usable in route loaders via queryClient.ensureQueryData) --------

export const libraryQuery = () => ({
  queryKey: queryKeys.library,
  queryFn: () => unwrap(api.GET("/api/v1/library")),
});

export const runSummaryQuery = (runId: string) => ({
  queryKey: queryKeys.runSummary(runId),
  queryFn: () => unwrap(api.GET("/api/v1/runs/{run_id}", { params: { path: { run_id: runId } } })),
});

export const runDetailsQuery = (runId: string) => ({
  queryKey: queryKeys.runDetails(runId),
  queryFn: () =>
    unwrap(api.GET("/api/v1/runs/{run_id}/details", { params: { path: { run_id: runId } } })),
});

export const stagesQuery = () => ({
  queryKey: queryKeys.stages,
  queryFn: () => unwrap(api.GET("/api/v1/stages")),
});

export const meQuery = () => ({
  queryKey: queryKeys.me,
  queryFn: () => unwrap(api.GET("/api/v1/me")),
});

// ---- Hooks -------------------------------------------------------------------------------

/** The library (machines, procedures, reviews, stats); polls at 5 s while anything processes. */
export function useLibrary(): UseQueryResult<Library, ApiError> {
  return useQuery<Library, ApiError>({
    ...libraryQuery(),
    refetchInterval: (query) => pollInterval(libraryIsActive(query.state.data)),
  });
}

/** One run's summary; polls at 5 s while the run is active. 404 → `error.isNotFound`. */
export function useRun(runId: string): UseQueryResult<RunSummary, ApiError> {
  return useQuery<RunSummary, ApiError>({
    ...runSummaryQuery(runId),
    refetchInterval: (query) => pollInterval(runIsActive(query.state.data)),
  });
}

/**
 * One run's details (clips, guide, dossier, …). Not polled: the server reads nine files per
 * request; refetch it when the summary's state changes or on demand.
 */
export function useRunDetails(runId: string, enabled = true): UseQueryResult<RunDetails, ApiError> {
  return useQuery<RunDetails, ApiError>({ ...runDetailsQuery(runId), enabled, staleTime: 30_000 });
}

/** The stage table (name, label, review_after, runner); effectively static. */
export function useStages(): UseQueryResult<StageInfo[], ApiError> {
  return useQuery<StageInfo[], ApiError>({ ...stagesQuery(), staleTime: Infinity });
}

/** The current user (a local stub until auth is enabled). */
export function useMe(): UseQueryResult<Me, ApiError> {
  return useQuery<Me, ApiError>({ ...meQuery(), staleTime: Infinity });
}
